import React, { useEffect, useMemo, useState } from 'react';
import { toast } from '../../../../../../../ui/toast';
import { createAssessmentTemplate } from '../../../../../../../utils/api_service/assessment_api';
import { AssessmentTemplateCreate, Question } from '../../../../../../../utils/types/assessment_types';

interface SaveTemplateProps {
    closeModal: () => void,
    onTemplateSave?: (templateData: AssessmentTemplateData) => void,
    questions: Question[],
    assessmentSettings?: AssessmentSettings,
    jobTitle: string,
    jobTitleId: string
}

interface AssessmentTemplateData {
    id?: string; // For updating existing templates
    name: string;
    description: string;
    category: string;
    tags: string[];
    questions: Question[];
    totalQuestions: number;
    totalPoints: number;
    estimatedTime: string;
    isPrivate: boolean;
    createdAt?: string;
    updatedAt?: string;
}

interface AssessmentSettings {
    attemptCount?: string;
    customMessage?: string;
    enableCopy?: boolean;
    showScore?: boolean;
    shuffleQuestions?: boolean;
    blurVideo?: boolean;
    unsuccessfulEmail?: boolean;
    thresholdPercent?: string;
}

interface ExistingTemplate {
    id: string;
    name: string;
    description: string;
    questionsCount: number;
    lastUsed: string;
    category: string;
    isPrivate: boolean;
}

// Mock existing templates - replace with API call
const existingTemplates: ExistingTemplate[] = [
  {
    id: '1',
    name: 'Frontend Developer Assessment',
    description: 'React, JavaScript, and CSS focused questions',
    questionsCount: 15,
    lastUsed: '2025-10-25',
    category: 'Engineering',
    isPrivate: false,
  },
  {
    id: '2',
    name: 'Backend Developer Assessment',
    description: 'Node.js, API, and database questions',
    questionsCount: 12,
    lastUsed: '2025-10-20',
    category: 'Engineering',
    isPrivate: false,
  },
  {
    id: '3',
    name: 'My Custom Assessment',
    description: 'Personal collection of questions',
    questionsCount: 8,
    lastUsed: '2025-10-18',
    category: 'Other',
    isPrivate: true,
  },
];

const categories = [
  'Engineering',
  'Design',
  'Product',
  'Data',
  'Marketing',
  'Sales',
  'Operations',
  'Finance',
  'HR',
  'Other',
];

export const SaveTemplate = ({
  closeModal,
  onTemplateSave,
  questions,
  assessmentSettings,
  jobTitle,
  jobTitleId,
}: SaveTemplateProps) => {
  const [saveMode, setSaveMode] = useState<'new' | 'existing'>('new');
  const [selectedTemplate, setSelectedTemplate] = useState<ExistingTemplate | null>(null);
  const [templateName, setTemplateName] = useState(jobTitle);
  const [templateDescription, setTemplateDescription] = useState(jobTitle);
  const [selectedCategory, setSelectedCategory] = useState('Other');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const now = new Date();
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = today.toLocaleString('en-GB', { month: 'short' }); // e.g. "Nov"
  const year = today.getFullYear();
  const formattedDate = `${day}/${month}/${year}`;
  const [saveObject, setSaveObject] = useState<AssessmentTemplateCreate>({
    templateTitle: `${jobTitle} - ${formattedDate}`,// 👈 jobTitle + date
    jobTitle: jobTitleId,
    questionAnswer: [],
  });

  useEffect(() => {
    if (questions.length > 0) {
      setSaveObject(prev => ({
        ...prev,
        questionAnswer: questions.map(q => q.id),
      }));
    }
  }, [questions]);

  const saveTemplate = async () => {
    try {
      const response = await createAssessmentTemplate(saveObject);
      if (response.ok) {
        toast.success('Template created successfully!', {
          position: 'bottom-center',
        });
        closeModal();
      } else {
        toast.error('Failed to create template', { position: 'bottom-center' });
      }
    } catch (e) {
      toast.error('Failed to create template', { position: 'bottom-center' });
    }

  };

  // Calculate template statistics
  const templateStats = useMemo(() => {
    const totalPoints = questions.reduce((sum, q) => sum + (q.points || 0), 0);
    const totalTimeSeconds = questions.reduce((sum, q) => sum + (q.time || 0), 0);
    const hours = Math.floor(totalTimeSeconds / 3600);
    const minutes = Math.floor((totalTimeSeconds % 3600) / 60);
    const estimatedTime = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

    return {
      totalQuestions: questions.length,
      totalPoints,
      estimatedTime,
    };
  }, [questions]);

  // Filter existing templates based on search
  const filteredTemplates = useMemo(() => {
    return existingTemplates.filter(template =>
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            template.description.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm]);

  // Handle template selection for updating
  const handleTemplateSelect = (template: ExistingTemplate) => {
    setSelectedTemplate(template);
    setTemplateName(template.name);
    setTemplateDescription(template.description);
    setSelectedCategory(template.category);
    setIsPrivate(template.isPrivate);
  };

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (newTag && !tags.includes(newTag) && tags.length < 8) {
        setTags([...tags, newTag]);
        setTagInput('');
      }
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  return (
    <div className='flex flex-col h-full w-full max-w-6xl mx-auto'>
      { /* Save Mode Selection */ }
      <div className='mb-6'>
        <label className='block text-sm font-medium text-gray-700 mb-3'>Save Options</label>
        <div className='grid grid-cols-2 gap-4'>
          <button
              onClick={() => {
                setSaveMode('new');
                setSelectedTemplate(null);
                setTemplateName('');
                setTemplateDescription('');
                setSelectedCategory('Other');
                setTags([]);
                setIsPrivate(false);
              }}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                saveMode === 'new'
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
          >
            <div className='flex items-center gap-3 mb-2'>
              <div className={`w-4 h-4 rounded-full border-2 ${
                saveMode === 'new'
                  ? 'border-indigo-500 bg-indigo-500'
                  : 'border-gray-300'
              }`}>
                { saveMode === 'new' && (
                  <div className='w-full h-full rounded-full bg-white transform scale-50'></div>
                ) }
              </div>
              <h3 className='font-medium text-gray-900'>Create New Template</h3>
            </div>
            <p className='text-sm text-gray-600 ml-7'>
              Create a brand new assessment template with current questions
            </p>
          </button>

          <button
              onClick={() => setSaveMode('existing')}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                saveMode === 'existing'
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
          >
            <div className='flex items-center gap-3 mb-2'>
              <div className={`w-4 h-4 rounded-full border-2 ${
                saveMode === 'existing'
                  ? 'border-indigo-500 bg-indigo-500'
                  : 'border-gray-300'
              }`}>
                { saveMode === 'existing' && (
                  <div className='w-full h-full rounded-full bg-white transform scale-50'></div>
                ) }
              </div>
              <h3 className='font-medium text-gray-900'>Update Existing Template</h3>
            </div>
            <p className='text-sm text-gray-600 ml-7'>
              Replace questions in an existing template
            </p>
          </button>
        </div>
      </div>

      { /* Existing Template Selection */ }
      { saveMode === 'existing' && (
        <div className='mb-6 p-4 bg-gray-50 rounded-lg'>
          <label className='block text-sm font-medium text-gray-700 mb-3'>
            Select Template to Update
          </label>
          <div className='mb-4'>
            <input
                type='text'
                placeholder='Search existing templates...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
            />
          </div>
          <div className='max-h-48 overflow-y-auto space-y-2'>
            { filteredTemplates.map((template) => (
              <div
                  key={template.id}
                  onClick={() => handleTemplateSelect(template)}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    selectedTemplate?.id === template.id
                      ? 'bg-indigo-100 border-2 border-indigo-500'
                      : 'bg-white border border-gray-200 hover:border-gray-300'
                  }`}
              >
                <div className='flex justify-between items-start'>
                  <div className='flex-1'>
                    <h4 className='font-medium text-gray-900'>{ template.name }</h4>
                    <p className='text-sm text-gray-600 mt-1'>{ template.description }</p>
                    <div className='flex items-center gap-4 mt-2 text-xs text-gray-500'>
                      <span>{ template.questionsCount } questions</span>
                      <span>Last used: { template.lastUsed }</span>
                      { template.isPrivate && (
                        <span
                            className='inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 rounded-full'>
                          <svg className='w-3 h-3' fill='currentColor' viewBox='0 0 20 20'>
                            <path fillRule='evenodd'
                                d='M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z'
                                clipRule='evenodd'/>
                          </svg>
                          Private
                        </span>
                      ) }
                    </div>
                  </div>
                  { selectedTemplate?.id === template.id && (
                    <div
                        className='w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center ml-3'>
                      <svg className='w-3 h-3 text-white' fill='currentColor' viewBox='0 0 20 20'>
                        <path fillRule='evenodd'
                            d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                            clipRule='evenodd'/>
                      </svg>
                    </div>
                  ) }
                </div>
              </div>
            )) }
            { filteredTemplates.length === 0 && (
              <div className='text-center py-8 text-gray-500'>
                <svg className='w-12 h-12 mx-auto mb-4 text-gray-400' fill='none' stroke='currentColor'
                    viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2}
                      d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'/>
                </svg>
                <p>No templates found matching your search</p>
              </div>
            ) }
          </div>
        </div>
      ) }

      { /* Form */ }
      <div className='flex-1 space-y-6'>
        { /* Template Name */ }
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            Template Name *
          </label>
          <input
              type='text'
              value={saveObject.templateTitle}
              onChange={(e) =>
                setSaveObject(prev => ({
                  ...prev,
                  templateTitle: e.target.value, // ✅ use e.target.value
                }))
              }
              placeholder='e.g., Senior Frontend Developer Assessment'
              className='w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
              maxLength={100}
          />
          <div className='text-xs text-gray-500 mt-1'>
            { templateName.length }/100 characters
          </div>
        </div>

        { /* Template Description */ }
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            Description *
          </label>
          <textarea
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              placeholder='Describe what this template is for and when to use it...'
              rows={3}
              className='w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none'
              maxLength={300}
          />
          <div className='text-xs text-gray-500 mt-1'>
            { templateDescription.length }/300 characters
          </div>
        </div>

        { /* Category */ }
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            Category
          </label>
          <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className='w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
          >
            { categories.map(category => (
              <option key={category} value={category}>{ category }</option>
            )) }
          </select>
        </div>

        { /* Tags */ }
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            Tags (Optional)
          </label>
          <div className='space-y-3'>
            <input
                type='text'
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                placeholder='Type a tag and press Enter (e.g., React, JavaScript, Senior Level)'
                className='w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                disabled={tags.length >= 8}
            />
            { tags.length > 0 && (
              <div className='flex flex-wrap gap-2'>
                { tags.map((tag, index) => (
                  <span
                      key={index}
                      className='inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-800 text-sm rounded-full'
                  >
                    { tag }
                    <button
                        onClick={() => removeTag(tag)}
                        className='text-indigo-600 hover:text-indigo-800'
                    >
                      <svg className='w-3 h-3' fill='currentColor' viewBox='0 0 20 20'>
                        <path fillRule='evenodd'
                            d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                            clipRule='evenodd'/>
                      </svg>
                    </button>
                  </span>
                )) }
              </div>
            ) }
            <div className='text-xs text-gray-500'>
              { tags.length }/8 tags added. Press Enter or comma to add tags.
            </div>
          </div>
        </div>

        { /* Privacy Setting */ }
        <div>
          <div className='flex items-start gap-3'>
            <div className='flex items-center h-5'>
              <input
                  id='private-template'
                  type='checkbox'
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                  className='w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2'
              />
            </div>
            <div>
              <label htmlFor='private-template' className='text-sm font-medium text-gray-700'>
                Make this template private
              </label>
              <p className='text-xs text-gray-500 mt-1'>
                Private templates are only visible to you. Uncheck to share with your team.
              </p>
            </div>
          </div>
        </div>

        { /* Assessment Summary */ }
        <div className='bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200'>
          <h4 className='text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2'>
            <svg className='w-5 h-5 text-blue-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2}
                  d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'/>
            </svg>
            Assessment Summary
          </h4>

          <div className='grid grid-cols-3 gap-6 mb-4'>
            <div className='text-center p-3 bg-white rounded-lg shadow-sm'>
              <div className='text-2xl font-bold text-blue-600'>{ templateStats.totalQuestions }</div>
              <div className='text-sm text-gray-600'>Questions</div>
            </div>
            <div className='text-center p-3 bg-white rounded-lg shadow-sm'>
              <div className='text-2xl font-bold text-green-600'>{ templateStats.totalPoints }</div>
              <div className='text-sm text-gray-600'>Total Points</div>
            </div>
            <div className='text-center p-3 bg-white rounded-lg shadow-sm'>
              <div className='text-2xl font-bold text-purple-600'>{ templateStats.estimatedTime }</div>
              <div className='text-sm text-gray-600'>Est. Time</div>
            </div>
          </div>

          <div className='bg-white rounded-lg p-4'>
            <h5 className='text-sm font-medium text-gray-900 mb-3'>Template will include:</h5>
            <div className='grid grid-cols-2 gap-4 text-sm'>
              <div className='space-y-2'>
                <div className='flex items-center gap-2'>
                  <svg className='w-4 h-4 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                    <path fillRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        clipRule='evenodd'/>
                  </svg>
                  <span className='text-gray-700'>All Questions & Answers</span>
                </div>
                <div className='flex items-center gap-2'>
                  <svg className='w-4 h-4 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                    <path fillRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        clipRule='evenodd'/>
                  </svg>
                  <span className='text-gray-700'>Skills & Difficulty Levels</span>
                </div>
                <div className='flex items-center gap-2'>
                  <svg className='w-4 h-4 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                    <path fillRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        clipRule='evenodd'/>
                  </svg>
                  <span className='text-gray-700'>Question Types & Media</span>
                </div>
              </div>
              <div className='space-y-2'>
                <div className='flex items-center gap-2'>
                  <svg className='w-4 h-4 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                    <path fillRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        clipRule='evenodd'/>
                  </svg>
                  <span className='text-gray-700'>Time Limits & Scoring</span>
                </div>
                <div className='flex items-center gap-2'>
                  <svg className='w-4 h-4 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                    <path fillRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        clipRule='evenodd'/>
                  </svg>
                  <span className='text-gray-700'>Question Order & Structure</span>
                </div>
                <div className='flex items-center gap-2'>
                  <svg className='w-4 h-4 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                    <path fillRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        clipRule='evenodd'/>
                  </svg>
                  <span className='text-gray-700'>Sub-skills & Themes</span>
                </div>
              </div>
            </div>
          </div>

          { saveMode === 'existing' && selectedTemplate && (
            <div className='mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg'>
              <div className='flex items-start gap-2'>
                <svg className='w-5 h-5 text-yellow-600 mt-0.5' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd'
                      d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                      clipRule='evenodd'/>
                </svg>
                <div>
                  <p className='text-sm font-medium text-yellow-800'>
                    Updating Existing Template
                  </p>
                  <p className='text-sm text-yellow-700 mt-1'>
                    This will replace all { selectedTemplate.questionsCount } questions
                    in &ldquo;{ selectedTemplate.name }&rdquo; with your
                    current { templateStats.totalQuestions } questions.
                  </p>
                </div>
              </div>
            </div>
          ) }
        </div>
      </div>

      { /* Action Buttons */ }
      <div className='flex justify-between items-center border-t border-gray-200 pt-6'>
        <button
            onClick={closeModal}
            className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50'
            disabled={isSaving}
        >
          Cancel
        </button>

        <div className='flex items-center gap-3'>
          { /* Save Mode Indicator */ }
          <div className='text-sm text-gray-600'>
            { saveMode === 'new' ? (
              <span className='flex items-center gap-1'>
                <svg className='w-4 h-4 text-green-600' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z'
                      clipRule='evenodd'/>
                </svg>
                Creating new template
              </span>
            ) : (
              <span className='flex items-center gap-1'>
                <svg className='w-4 h-4 text-blue-600' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd'
                      d='M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z'
                      clipRule='evenodd'/>
                </svg>
                Updating existing template
              </span>
            ) }
          </div>

          <button
              onClick={saveTemplate}
              disabled={
                !templateName.trim() ||
                            !templateDescription.trim() ||
                            (saveMode === 'existing' && !selectedTemplate) ||
                            isSaving ||
                            questions.length === 0
              }
              className={`px-6 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
                templateName.trim() &&
                            templateDescription.trim() &&
                            (saveMode === 'new' || selectedTemplate) &&
                            questions.length > 0 &&
                            !isSaving
                  ? saveMode === 'new'
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
          >
            { isSaving && (
              <svg className='animate-spin w-4 h-4' fill='none' viewBox='0 0 24 24'>
                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor'
                    strokeWidth='4'/>
                <path className='opacity-75' fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'/>
              </svg>
            ) }
            { isSaving
              ? (saveMode === 'new' ? 'Creating Template...' : 'Updating Template...')
              : (saveMode === 'new' ? 'Create Template' : 'Update Template')
            }
          </button>
        </div>
      </div>
    </div>
  );
};
